import { EventListener, type FocusControl } from "silver";

const ANIMATION_DURATION = 510;

const CLASS_ROOT = "sidesheet";
const CLASS_CONTAINER = "sidesheet_contentContainer";
const CLASS_ROOT_VISIBLE = "-visible";
const CLASS_DOC_NOSCROLL = "sidesheet_noscroll";

type SidesheetOptions = {
	dismissable: boolean;
	size: "medium"|"small"|"large"
}

export class Sidesheet {

	public readonly onDismiss = EventListener.create<{ sender: any }>("requestDismiss");
	public readonly element: HTMLElement = document.createElement("div");
	private readonly contentContainer: HTMLElement = document.createElement("div");
	private hideTimeoutHandle: number = -1;
	private dismissable: boolean = false;
	private lastResolver: (null|(() => void)) = null;

	constructor(
		private focusControl: FocusControl,
	) {
		this.element.classList.add(CLASS_ROOT);
		this.contentContainer.classList.add(CLASS_CONTAINER);

		this.element.appendChild(this.contentContainer);

		this.element.addEventListener("click", (e) => {
			if (e.target === this.element && this.dismissable) {
				this.hide(this);
			}
		});
	}

	public async show(content: Node, options: Partial<SidesheetOptions> = { }): Promise<void> {
		if (this.lastResolver) {
			if (!this.dismissable) {
				throw new Error("Cannot automatically hide open sidesheet");
			}
			await this.hide();
		}

		this.element.classList.toggle("-medium", options.size === "medium");
		this.element.classList.toggle("-small", options.size === "small");
		this.element.classList.toggle("-large", options.size === "large");
		this.dismissable = options.dismissable ?? false;
		this.contentContainer.appendChild(content);
		document.body.appendChild(this.element);

		return new Promise(resolve => {
			document.body.classList.add(CLASS_DOC_NOSCROLL);
			setTimeout(() => {
				this.element.classList.add(CLASS_ROOT_VISIBLE);
			}, 1);
			clearTimeout(this.hideTimeoutHandle);
			this.lastResolver = resolve;
			this.focusControl.push(this.contentContainer, () => {
				if (this.dismissable) {
					this.hide(this);
				}
			});
		});
	}

	public async hide(sender?: any): Promise<void> {
		if (!this.lastResolver) {
			console.warn("Nothing to hide");
			return;
		}
		this.element.classList.remove(CLASS_ROOT_VISIBLE);
		clearTimeout(this.hideTimeoutHandle);
		this.onDismiss.trigger({ sender });
		this.focusControl.pop();
		this.lastResolver();
		this.lastResolver = null;
		return new Promise(resolve => {
			this.hideTimeoutHandle = setTimeout(() => {
				if (this.element.parentNode) {
					this.element.parentNode.removeChild(this.element);
				}
				this.contentContainer.innerHTML = "";
				document.body.classList.remove(CLASS_DOC_NOSCROLL);
				resolve();
			}, ANIMATION_DURATION);
		});
	}
}
